'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT, HAIRLINE, PageHeader, DisplayTitle } from '../_layout';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';

type GLActivity = { id: number; featured_rank?: number | null; slots: { end_time: string }[]; title: string; subtitle?: string | null; featured_lede?: string | null };

// upcoming 块状态文案（按语言）
const GL_TXT = {
  CN: { activeKicker: '正在进行', recapKicker: '刚刚结束', activeCta: '查看完整行程 →', recapCta: '查看巡演回顾 →' },
  EN: { activeKicker: 'Now booking', recapKicker: 'Just concluded', activeCta: 'View full tour →', recapCta: 'Read the recap →' },
  DE: { activeKicker: 'Vorverkauf', recapKicker: 'Gerade beendet', activeCta: 'Tour ansehen →', recapCta: 'Tour-Rückblick lesen →' },
} as const;

const COPY = {
  CN: {
    kicker: '音乐 · GraviLens 引力透镜',
    title: '把中国独立音乐\n带到欧洲。',
    lede: 'GraviLens 是 PathIN 的音乐板块。我们做四件事：巡演策划、宣传发行、跨境合作、长期出版——一切围绕一个目标，让中国独立音乐人的作品有效地走向欧洲。',
    services: {
      title: '我们提供',
      items: [
        { kicker: '01', title: '巡演与现场', body: '从路线规划、场地对接、票务到现场协调。可主办、共同主办或落地协助欧洲多城联动。' },
        { kicker: '02', title: '宣传与叙事', body: '简介、媒体物料、视觉与渠道策略。让乐队的故事在欧洲场景里能被听懂。' },
        { kicker: '03', title: '发行支持', body: '单曲 / EP / 专辑的上线节奏、数字平台呈现、宣传素材协调。' },
        { kicker: '04', title: '跨境合作', body: '对接欧洲艺人、厂牌、音乐节，做联合演出夜或共担巡演的设计。' },
        { kicker: '05', title: '长期发展', body: '可持续巡演与听众积累——不止做一场，更看一年、五年。' },
      ],
    },    past: {
      kicker: '过往合作',
      lede: '已经完成的几场——可以代表我们的工作方式。',
      items: [
        { date: '2026 · 3 月', title: '万能青年旅店 · 2026 欧洲巡演德国站', body: '万青首度欧洲巡演，德国站只有柏林这一晚。Myna Stage × GraviLens 联合呈现，开票当日即售罄。', href: '/v2/events/oys-berlin-2026' },
        { date: '2025 · 6 月', title: 'An Corporation @ ACUD MACHT NEU', body: '上海后摇 An Corporation 落地柏林 ACUD，与 Dusk Saffron、gossamer sea 同台。', href: '/v2/events/an-corporation' },
        { date: '2025 · 5 月', title: 'FAZI 法兹 欧洲巡演 柏林 / 科隆', body: 'Maschinenhaus + Blue Shell 两站，与 Positive Transmitter、Nugget Records 共同主办。', href: '/v2/events/fazi-tour' },
      ],
    },
    publication: { kicker: '长期出版', title: '《中国乐队欧洲巡演手册》', body: '一本面向中国独立乐队的实操手册——从签证、签约、运输、到欧洲场地与节展生态。五章 + 附录，公益出版，编纂中。' },
    contact: { kicker: '合作', body: '如果你是音乐人、厂牌、场地、媒体——欢迎写信。我们认真回复每一封。', cta: '写信给 GraviLens →' },
  },
  EN: {
    kicker: 'Music · GraviLens 引力透镜',
    title: "Bringing China's\nindependent music\nto Europe.",
    lede: 'GraviLens is the music side of PathIN. Four things: tour planning, promotion & releases, cross-border collaboration, long-form publishing — all aimed at moving Chinese independent music effectively into Europe.',
    services: {
      title: 'What we do',
      items: [
        { kicker: '01', title: 'Tours & Live', body: 'Routing, venues, ticketing, on-site coordination. We can host, co-host, or provide on-the-ground support for multi-city European tours.' },
        { kicker: '02', title: 'Promotion & Story', body: "Bios, press materials, visual and channel strategy. Make a band's story land in European contexts." },
        { kicker: '03', title: 'Release support', body: 'Single / EP / album rollout pacing, digital platform presence, supporting press materials.' },
        { kicker: '04', title: 'Cross-border', body: 'Connecting with European artists, labels, festivals — designing joint shows or shared tour legs.' },
        { kicker: '05', title: 'Long horizon', body: 'Sustainable tours and audience growth — not just one show, but the next year and the year after.' },
      ],
    },    past: {
      kicker: 'Past tours',
      lede: 'A handful of finished tours — they show how we work.',
      items: [
        { date: '2026 · March', title: 'Omnipotent Youth Society · 2026 UK & EU Tour · Berlin', body: "OYS's first European tour. Berlin was the only German date. Co-presented by Myna Stage and GraviLens — sold out the day tickets opened.", href: '/v2/events/oys-berlin-2026' },
        { date: '2025 · June', title: 'An Corporation @ ACUD MACHT NEU', body: 'Shanghai post-rock landed in Berlin ACUD, with local Dusk Saffron and gossamer sea.', href: '/v2/events/an-corporation' },
        { date: '2025 · May', title: 'FAZI · European Tour, Berlin / Cologne', body: 'Maschinenhaus + Blue Shell, co-presented with Positive Transmitter & Nugget Records.', href: '/v2/events/fazi-tour' },
      ],
    },
    publication: { kicker: 'Long-form publishing', title: 'China-band Europe Tour Handbook', body: 'A practical handbook for Chinese independent bands — from visas and contracts to logistics and the European venue / festival ecosystem. Five chapters + appendix. Public-good publication, in progress.' },
    contact: { kicker: 'Work with us', body: "Musician, label, venue, press — write to us. We answer every email seriously.", cta: 'Email GraviLens →' },
  },
  DE: {
    kicker: 'Musik · GraviLens 引力透镜',
    title: 'Chinas unabhängige\nMusik nach Europa\nbringen.',
    lede: 'GraviLens ist der Musik-Bereich von PathIN. Wir machen vier Dinge: Tourplanung, Promotion & Releases, grenzübergreifende Zusammenarbeit, Langform-Publishing — alles mit dem Ziel, chinesische Independent-Musik wirkungsvoll nach Europa zu tragen.',
    services: {
      title: 'Was wir tun',
      items: [
        { kicker: '01', title: 'Touren & Live', body: 'Routing, Venues, Ticketing, Vor-Ort-Koordination. Wir können veranstalten, co-veranstalten oder Mehr-Städte-Touren in Europa unterstützen.' },
        { kicker: '02', title: 'Promotion & Story', body: 'Bios, Pressematerial, visuelle und Kanal-Strategie — damit die Geschichte der Band im europäischen Kontext ankommt.' },
        { kicker: '03', title: 'Release-Support', body: 'Single / EP / Album-Rollouts, digitale Plattformen, begleitendes Pressematerial.' },
        { kicker: '04', title: 'Grenzübergreifend', body: 'Vernetzung mit europäischen Künstler:innen, Labels, Festivals — gemeinsame Konzerte oder geteilte Tour-Abschnitte.' },
        { kicker: '05', title: 'Langer Atem', body: 'Nachhaltige Touren und Publikumsaufbau — nicht nur ein Konzert, sondern das nächste Jahr und das danach.' },
      ],
    },    past: {
      kicker: 'Vergangene Touren',
      lede: 'Einige abgeschlossene Touren — sie zeigen, wie wir arbeiten.',
      items: [
        { date: '2026 · März', title: 'Omnipotent Youth Society · 2026 UK & EU Tournee · Berlin', body: 'OYS gab ihre erste Europatournee. Berlin war der einzige Termin in Deutschland. Co-präsentiert von Myna Stage und GraviLens — am Verkaufstag ausverkauft.', href: '/v2/events/oys-berlin-2026' },
        { date: '2025 · Juni', title: 'An Corporation @ ACUD MACHT NEU', body: 'Shanghaier Post-Rock im Berliner ACUD, mit Dusk Saffron und gossamer sea.', href: '/v2/events/an-corporation' },
        { date: '2025 · Mai', title: 'FAZI · Europatournee, Berlin / Köln', body: 'Maschinenhaus + Blue Shell, gemeinsam mit Positive Transmitter und Nugget Records präsentiert.', href: '/v2/events/fazi-tour' },
      ],
    },
    publication: { kicker: 'Langform-Publishing', title: 'China-Band-Europatournee-Handbuch', body: 'Ein praktisches Handbuch für chinesische Independent-Bands — von Visa und Verträgen über Logistik bis zum europäischen Venue- und Festival-Ökosystem. Fünf Kapitel + Anhang. Gemeinwohl-Publikation, in Arbeit.' },
    contact: { kicker: 'Zusammenarbeit', body: 'Musiker:in, Label, Venue, Presse — schreiben Sie uns. Wir antworten ernsthaft auf jede Nachricht.', cta: 'GraviLens schreiben →' },
  },
};

export default function V2GraviLens() {
  const lang = useV2Lang();
  const t = COPY[lang];
  const gtx = GL_TXT[lang];
  const [events, setEvents] = useState<GLActivity[]>([]);
  useEffect(() => {
    const apiLang = appLanguageToContentLocale(lang);
    fetch(`/api/activities?language=${apiLang}&pageSize=50`)
      .then((r) => r.json())
      .then((d) => setEvents(Array.isArray(d) ? d : d?.activities ?? []))
      .catch(() => setEvents([]));
  }, [lang]);
  const featured = [...events].filter((e) => e.featured_rank != null).sort((a, b) => a.featured_rank! - b.featured_rank!)[0];
  const featState: 'active' | 'recap' | null = !featured ? null
    : Math.max(...featured.slots.map((s) => new Date(s.end_time.replace(' ', 'T')).getTime())) >= Date.now() ? 'active' : 'recap';

  return (
    <V2Layout hideFooterContact>
      <PageHeader kicker={t.kicker} title={<DisplayTitle text={t.title} />} lede={t.lede} />

      <Rule />

      {/* Services / What we do */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px]">
          <h2 className="v2-cjk-tight text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.025em] font-bold mb-12">{t.services.title}</h2>
          <div className="grid gap-x-12 md:grid-cols-2 border-t" style={{ borderColor: HAIRLINE }}>
            {t.services.items.map((s) => (
              <div key={s.kicker} className="border-b py-10 md:py-12 md:pr-8" style={{ borderColor: HAIRLINE }}>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: ACCENT }}>{s.kicker}</span>
                <h3 className="v2-cjk-tight mt-3 text-[clamp(22px,2vw,30px)] leading-[1.25] tracking-[-0.02em] font-bold">{s.title}</h3>
                <p className="mt-4 text-[15px] leading-[1.7] text-white/80 max-w-[55ch]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </RevealSection>

      {/* Upcoming — 数据驱动；无 featured 时整块隐藏 */}
      {featured && (
        <>
          <Rule />
          <RevealSection className="px-6 md:px-10 py-24">
            <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
              <div className="md:col-span-3"><Eyebrow accent>{featState === 'active' ? gtx.activeKicker : gtx.recapKicker}</Eyebrow></div>
              <div className="md:col-span-9">
                <Link to={`/events/${featured.id}`} className="group block">
                  <h3 className="v2-cjk-tight text-[clamp(32px,4vw,60px)] leading-[1.1] tracking-[-0.035em] font-extrabold">{featured.title}</h3>
                  {(featured.subtitle || featured.featured_lede) && <p className="mt-4 text-[15px] font-mono tracking-wide text-white/75 font-medium">{featured.subtitle || featured.featured_lede}</p>}
                  <span className="inline-block mt-6 text-[14px] border-b-4 pb-2 font-semibold tracking-wide" style={{ borderColor: ACCENT, color: ACCENT }}>{featState === 'active' ? gtx.activeCta : gtx.recapCta}</span>
                </Link>
              </div>
            </div>
          </RevealSection>
        </>
      )}

      <Rule />

      {/* Past tours */}
      <RevealSection className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3">
            <Eyebrow>{t.past.kicker}</Eyebrow>
            <p className="mt-4 text-[13px] leading-[1.55] text-white/65 max-w-[28ch]">{t.past.lede}</p>
          </div>
          <div className="md:col-span-9 border-t" style={{ borderColor: HAIRLINE }}>
            {t.past.items.map((it) => (
              <Link
                key={it.title}
                to={it.href}
                className="grid grid-cols-12 gap-x-6 gap-y-2 py-8 md:py-10 border-b items-start group hover:bg-white/[0.02] transition-colors px-1"
                style={{ borderColor: HAIRLINE }}
              >
                <span className="col-span-12 md:col-span-2 font-mono text-[12px] uppercase tracking-[0.15em] text-white/65 mt-1.5 font-medium">
                  {it.date}
                </span>
                <div className="col-span-12 md:col-span-9">
                  <h4 className="v2-cjk-tight text-[clamp(20px,1.8vw,26px)] leading-[1.25] tracking-[-0.015em] font-semibold text-white">
                    {it.title}
                  </h4>
                  <p className="mt-3 text-[14px] leading-[1.65] text-white/75 max-w-[60ch]">
                    {it.body}
                  </p>
                </div>
                <span
                  className="hidden md:block col-span-1 text-right text-[14px] font-mono mt-2"
                  style={{ color: ACCENT }}
                >
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Long-form publication */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.publication.kicker}</Eyebrow></div>
          <Link to="/GraviLens/handbook" className="md:col-span-9 group block">
            <h3 className="v2-cjk-tight text-[clamp(28px,3.6vw,48px)] leading-[1.15] tracking-[-0.035em] font-extrabold max-w-[20ch] text-white">{t.publication.title}</h3>
            <p className="mt-6 text-[16px] leading-[1.7] text-white/80 max-w-[60ch]">{t.publication.body}</p>
            <span
              className="inline-block mt-8 text-[14px] border-b-4 pb-2 font-semibold tracking-wide transition-all group-hover:translate-x-1"
              style={{ borderColor: ACCENT, color: ACCENT }}
            >
              {lang === 'CN' ? '查看手册页 →' : lang === 'DE' ? 'Handbuch-Seite ansehen →' : 'View handbook page →'}
            </span>
          </Link>
        </div>
      </RevealSection>

      <Rule />

      {/* Contact */}
      <RevealSection className="px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] grid gap-12 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow accent>{t.contact.kicker}</Eyebrow></div>
          <div className="md:col-span-9">
            <p className="text-[clamp(20px,2vw,28px)] leading-[1.4] text-white/85 max-w-[40ch]">{t.contact.body}</p>
            <a href="mailto:gravilens@pathin.eu" className="inline-block mt-8 text-[15px] border-b-4 pb-2 font-semibold tracking-wide hover:no-underline" style={{ borderColor: ACCENT, color: ACCENT }}>
              {t.contact.cta}
            </a>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}
